import Cookies from "../utilities/cookies";
import StatefulSettings, { STATE_KEY_TOUR_STATE } from "../controllers/StatefulSettings";
import Database from "../utilities/database";
import ConfigCache from "../utilities/ConfigCache";
import GuestSettings from "../controllers/GuestSettings";
import { ControlRecord, IShowNotificationState, AuditMessage, TourStep } from "../ts/Definitions";
import { AuditActionTypes, TraceLevel } from "../ts/enums";
import { getLocation } from "../utilities/externalApiUtils";
import Timer from "../utilities/Timer";
import { Auth } from "firebase/auth";
import { Cookies as reactCookie } from "react-cookie";

export class SessionManager {
  private database!: Database;
  private cookiesChangedListeners: (() => void)[] = [];
  private loggedOnCallbacks: ((success: boolean, isNew: boolean) => void)[] = [];
  private userSettingsCallbacks: ((userSettings?: StatefulSettings) => void)[] = [];
  private userSettings: StatefulSettings | undefined = undefined;
  public cookiesService: Cookies = new Cookies();
  public showMessage: (args: IShowNotificationState) => void = () => ({});
  private isLoggedOn = false;
  private fbAuth!: Auth;
  private userSettingsCache: Record<string, StatefulSettings> = {};
  private configCache!: ConfigCache;
  private guestSettings!: GuestSettings;
  private minuteTimer!: Timer;
  private initialised = false;

  private handleMinuteTick = (): void => {
    if (this.userSettings) this.userSettings.heartbeat();
  };

  public initialize(
    database: Database,
    fbAuth: Auth,
    configCache: ConfigCache,
    guestSettings: GuestSettings,
    minuteTimer: Timer
  ): void {
    this.database = database;
    this.fbAuth = fbAuth;
    this.configCache = configCache;
    this.guestSettings = guestSettings;
    this.minuteTimer = minuteTimer;
  }

  public initialise(
    cookies: reactCookie,
    showMessage: (args: IShowNotificationState) => void
  ): void {
    if (this.initialised) return;
    this.initialised = true;
    this.cookiesService.initialise(cookies, () => {
      this.cookiesChangedListeners.forEach((listener) => {
        listener();
      });
    });
    this.showMessage = showMessage;
    this.guestSettings.initialise(this.cookiesService, this.showMessage);
  }

  public addCookieChangedListener(callback: () => void): void {
    this.cookiesChangedListeners.push(callback);
  }

  public unsubscribeCookieChangedListener(callback: () => void): void {
    this.cookiesChangedListeners.splice(
      this.cookiesChangedListeners.indexOf(callback),
      1
    );
  }

  public userLogout(): void {
    Object.keys(this.userSettingsCache).forEach((user) =>
      this.userSettingsCache[user].dispose()
    );
    this.userSettingsCache = {};
    if (this.userSettings) this.userSettings.dispose();
    this.userSettings = undefined;
  }

  public userLoggedOn(
    userRecord: ControlRecord,
    isNew: boolean,
    onStateAvailable: () => void
  ): void {
    if (!this.cookiesService || !userRecord) return;
    this.database.setCurrentUser(userRecord);
    void this.configCache.fetchConfig().catch((error) =>
      console.error("Failed to fetch config:", error)
    );
    this.cookiesService.username = userRecord.username;
    this.cookiesService.userid = userRecord.userid;
    Object.keys(this.userSettingsCache).forEach((s) =>
      this.userSettingsCache[s].dispose()
    );
    this.userSettingsCache = {};

    const userSettingsInstance = this.getUserSettings(userRecord.userid);
    if (this.userSettings) {
      this.userSettings.dispose();
    }

    this.userSettings = userSettingsInstance;
    if (!this.minuteTimer.handle) this.minuteTimer.start();

    this.isLoggedOn = true;

    // only do this is the user is not new
    if (!isNew) {
      // we want to kick off a scheduler when the user is logged in and pull back their already configured state
      if (userSettingsInstance.isLoaded()) onStateAvailable();
      else userSettingsInstance.subscribeToLoaded(onStateAvailable);
    }

    void getLocation().then((location) => {
      if (!this.userSettings) return;
      const control = this.userSettings.getControl();
      control.username = userRecord.username;
      control.userid = userRecord.userid;
      void this.database
        .auditUser(
          [
            new AuditMessage(
              AuditActionTypes.User,
              "Log On",
              `User ${userRecord.username} Logged On v${import.meta.env.VITE_VERSION
              }. (${location?.ipAddress})`,
              TraceLevel.info,
              true
            ),
          ],
          control
        )
        .catch((error) => console.error("Failed to audit user login:", error));
    });

    localStorage.setItem("sessionTime", new Date().getTime().toString());

    this.loggedOnCallbacks.forEach((callback) =>
      callback(this.isLoggedOn, isNew)
    );
  }

  public getLoggedOnUserSettings(): StatefulSettings {
    if (!this.isLoggedOn)
      return this.guestSettings as unknown as StatefulSettings;

    if (!this.userSettings) {
      this.userSettings = this.getUserSettings(this.cookiesService.userid);
    }
    return this.userSettings;
  }

  public async getSettingsAndThen(
    userid: string,
    callback: (settings: StatefulSettings) => void
  ): Promise<void> {
    const settings = this.getUserSettings(userid);
    if (settings.isLoaded()) callback(settings);
    else {
      const promise = new Promise<boolean>((resolve) => {
        settings.subscribeToLoaded(() => resolve(true));
        if (settings.isLoaded()) resolve(true);
      });
      await promise;
      callback(this.getUserSettings(userid));
    }
  }

  public getUserSettings(userid: string): StatefulSettings {
    if (this.userSettingsCache[userid]) return this.userSettingsCache[userid];
    this.userSettingsCache[userid] = new StatefulSettings(
      userid,
      this.database,
      this.cookiesService,
      this.showMessage,
      this.configCache
    );
    return this.userSettingsCache[userid];
  }

  public subscribeToLogOn(
    callback: (success: boolean, isNew: boolean) => void
  ): void {
    if (callback) this.loggedOnCallbacks.push(callback);
  }

  public unsubscribeToLogOn(
    callback: (success: boolean, isNew: boolean) => void
  ): void {
    const loggedOnIdx = this.loggedOnCallbacks.indexOf(callback);
    if (loggedOnIdx > -1) this.loggedOnCallbacks.splice(loggedOnIdx, 1);
  }

  public subscribeToUserSettings(
    callback: (userSettings?: StatefulSettings) => void
  ): void {
    if (callback) this.userSettingsCallbacks.push(callback);
    if (
      this.isLoggedOn &&
      callback !== undefined &&
      this.userSettings &&
      !this.userSettings.isSettingsLoaded()
    )
      callback(this.userSettings);
  }

  public unsubscribeToUserSettings(
    callback: (userSettings?: StatefulSettings) => void
  ): void {
    const loggedOnIdx = this.userSettingsCallbacks.indexOf(callback);
    if (loggedOnIdx > -1) this.userSettingsCallbacks.splice(loggedOnIdx, 1);
  }

  public notifyUserSettingsCallbacks(): void {
    this.userSettingsCallbacks.forEach((callback) =>
      callback(this.userSettings)
    );
  }

  public getUserControl(userid: string): ControlRecord | undefined {
    return this.userSettings
      ?.getAllControlRecords()
      .find((c) => c.userid === userid);
  }

  public addTourStep(
    screen: string,
    steps: TourStep[],
    stepToAdd: TourStep
  ): void {
    if (steps.find((s) => s.selector === stepToAdd.selector)) return;
    const settings = this.getLoggedOnUserSettings();
    const tourState = settings.getState<Record<string, string[]>>(
      STATE_KEY_TOUR_STATE,
      {}
    );
    const currentSteps = tourState[screen];
    if (!currentSteps) {
      steps.push(stepToAdd);
    } else {
      if (!currentSteps.includes(stepToAdd.selector)) steps.push(stepToAdd);
    }
  }

  public updateTourState(screen: string, step: TourStep): void {
    const settings = this.getLoggedOnUserSettings();
    const tourState = settings.getState<Record<string, string[]>>(
      STATE_KEY_TOUR_STATE,
      {}
    );
    let currentSteps = tourState[screen];
    if (!currentSteps) {
      currentSteps = [step.selector];
      tourState[screen] = currentSteps;
    } else {
      if (!currentSteps.includes(step.selector))
        currentSteps.push(step.selector);
    }
    settings.updateState(STATE_KEY_TOUR_STATE, tourState);
  }

  public getLoggedOnUser() {
    return this.cookiesService;
  }

  public isUserLoggedOn(): boolean {
    return this.isLoggedOn;
  }

  public getCurrentUserSettings(): StatefulSettings | undefined {
    return this.userSettings;
  }
}
